import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, Button } from '../../../../Components/uielements';
import BioPlusTextField from '../../../../Components/form-fields/BioPlusTextField';
import { Form } from '@kuzmycz/react-form-ula';
import {
  ModalButtonWrapper,
  ModalGap,
  ModalWrapper,
  OutlineButton,
} from '../../../../services/settings/appStyle/global.style';
import { ChangePassword } from '../../../../services/bioplus-services/auth';
import { ClosedEye, OpenEye } from '../../../../Components/icons';

// Styled Components
export const PasswordWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const EyeButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }

  &:focus {
    outline: none;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  margin-top: 8px;
  font-size: 13px;
  color: #f87171;
`;

const PasswordRequirements = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
`;

const ResetPasswordModal = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleChangePassword = async (values) => {
    // Validate that passwords are provided
    if (!values.oldPassword || !values.newPassword) {
      setError('Both password fields are required.');
      return;
    }

    // Validate new password meets minimum requirements
    if (values.newPassword.length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }

    const alphabeticCount = (values.newPassword.match(/[a-zA-Z]/g) || []).length;
    if (alphabeticCount < 4) {
      setError('New password must contain at least 4 alphabetic characters.');
      return;
    }

    const numericCount = (values.newPassword.match(/[0-9]/g) || []).length;
    if (numericCount < 1) {
      setError('New password must contain at least 1 numeric character.');
      return;
    }

    const specialCharCount = (values.newPassword.match(/[^a-zA-Z0-9]/g) || []).length;
    if (specialCharCount < 1) {
      setError('New password must contain at least 1 special character.');
      return;
    }

    try {
      setError('');
      setLoading(true);
      await ChangePassword(values.oldPassword, values.newPassword);
      alert('Password changed successfully.');
      onClose();
    } catch (err) {
      setError('Password change failed. Please check your current password and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Change Password">
      <Form initialValues={{ oldPassword: '', newPassword: '' }} onSubmit={handleChangePassword}>
        <ModalWrapper>
          <ModalGap>
            {/* Old Password Field */}
            <PasswordWrapper>
              <BioPlusTextField
                label="Current Password"
                name="oldPassword"
                type={showOldPassword ? 'text' : 'password'}
                fullWidth
                autoComplete="current-password"
              />
              <EyeButton
                type="button"
                onClick={() => setShowOldPassword((v) => !v)}
                aria-label="Toggle current password visibility"
              >
                {showOldPassword ? <OpenEye /> : <ClosedEye />}
              </EyeButton>
            </PasswordWrapper>

            {/* New Password Field */}
            <PasswordWrapper>
              <BioPlusTextField
                label="New Password"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                autoComplete="new-password"
              />
              <EyeButton
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                aria-label="Toggle new password visibility"
              >
                {showNewPassword ? <OpenEye /> : <ClosedEye />}
              </EyeButton>
            </PasswordWrapper>

            {/* Password Requirements Info */}
            <PasswordRequirements>
              Minimum 6 characters · 4 letters · 1 numbers · 1 special characters
            </PasswordRequirements>

            {/* Error Message */}
            {error && <ErrorText>{error}</ErrorText>}
          </ModalGap>

          {/* Action Buttons */}
          <ModalButtonWrapper>
            <OutlineButton type="button" onClick={onClose} disabled={loading}>
              Cancel
            </OutlineButton>
            <Button varient="normal" type="submit" disabled={loading}>
              {loading ? 'Changing…' : 'Change Password'}
            </Button>
          </ModalButtonWrapper>
        </ModalWrapper>
      </Form>
    </Modal>
  );
};

export default ResetPasswordModal;
